import React, {cloneElement, createContext, PropsWithChildren, ReactComponentElement, ReactElement, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Popup, {Position, ToolbarItem} from "devextreme-react/popup";
import Guid from "../../models/Guid";

interface IPopupPageProps<TReturnObject> {
    titre: string,
    isPopupOpen: boolean,
    onClose: (value?: TReturnObject) => void,
    onSelection?: (value: TReturnObject) => void,
    width: number | string;
    height: number | string;
    mode: "ajout" | "liste"
    children: ReactComponentElement<any>;
    showOk?: boolean;
    showAnnuler?: boolean;
    isModeModifier?: boolean
}

type PopupPagecontextType = {
    selectedValue?: any,
    setSelectedValue?: (value: any, closePopup?: boolean) => void,
    isContextInitialized: boolean,
    isPopupOpen: boolean,
    doitSauvegarder?: boolean,
    setDoitSauvegarder?: (value: boolean) => void,
    onClose?: (value?: any) => void
}

const PopupPageContext = createContext<PopupPagecontextType>({
    selectedValue: undefined,
    setSelectedValue: undefined,
    isContextInitialized: false,
    isPopupOpen: false,
    doitSauvegarder: false,
    setDoitSauvegarder: undefined,
    onClose: undefined
});
const useContextPopupPage = () => useContext(PopupPageContext);
export {useContextPopupPage}

function PopupPage<TReturnObject = Guid>({children, isPopupOpen, onClose, onSelection, width, height, mode, titre, showOk = true, showAnnuler = true, isModeModifier = true}: IPopupPageProps<TReturnObject | undefined>) {
    const [value, setValue] = useState<TReturnObject>();
    const childRef = useRef<any>(null);
    const [doitSauvegarder, setDoitSauvegarder] = useState(false)
    const [hasClickOkOrAnnuler, setHasClickOkOrAnnuler] = useState(false)
    const annulerButtonOption = useMemo(() => {
        return {
            text: "Annuler",
            type: "default",
            stylingMode: "contained",
            onClick: () => {
                onClose();
            }
        }
    }, [onClose])

    const selectionnerButtonOption = useMemo(() => {
        return {
            text: mode == "ajout" ? "Ok" : "Sélectionner",
            type: "default",
            stylingMode: "contained",
            onClick: async () => {
                setHasClickOkOrAnnuler(true);
                let newValue = value;
                let row;
                if (childRef?.current?.sauvegarder) {
                    row = await childRef?.current.sauvegarder();
                }
                if (childRef.current?.getData) {
                    newValue = await childRef?.current.getData();
                }

                if (!doitSauvegarder) {
                    if (onSelection) {
                        onSelection(row || newValue);
                    } else
                        onClose(newValue)
                }
            }
        }
    }, [value, onSelection, onClose, mode, doitSauvegarder])

    useEffect(() => {
        setHasClickOkOrAnnuler(false);
    }, [isPopupOpen])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isPopupOpen) {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isPopupOpen, onClose])

    const childrenWithRef = useMemo(() => {
        if (React.isValidElement(children)) {
            return cloneElement(children, {...children.props, saveBtnRef: childRef})
        }
        return children;
    }, [children]);
    if (!isPopupOpen) return null

    return (
        <Popup title={titre} width={width} height={height} dragEnabled={true} animation={undefined} visible={isPopupOpen} onHiding={() => onClose(value)} style={{marginBottom: "5px"}}>
            <Position
                my="center"
                collision="fit"
            />
            <PopupPageContext.Provider value={{
                selectedValue: value,
                setSelectedValue: (value: TReturnObject, closePopup) => {
                    //if (mode != "liste") return;

                    setValue(value)
                    if (onSelection && closePopup) {
                        onSelection(value);
                    }
                },
                isContextInitialized: true,
                isPopupOpen: isPopupOpen,
                doitSauvegarder: doitSauvegarder,
                setDoitSauvegarder: setDoitSauvegarder,
                onClose: () => {
                    hasClickOkOrAnnuler && onClose()
                }
            }}>
                {childrenWithRef}
            </PopupPageContext.Provider>
            {showOk && isModeModifier &&
                <ToolbarItem
                    widget="dxButton"
                    toolbar="bottom"
                    location="after"
                    options={selectionnerButtonOption}
                />
            }
            {showAnnuler &&
                <ToolbarItem
                    widget="dxButton"
                    toolbar="bottom"
                    location="after"
                    options={annulerButtonOption}
                />
            }
        </Popup>
    );
}

export default PopupPage;
const usePopupProps = () => useContext(PopupPageContext);
export {usePopupProps};